import { Box, Text, HStack, VStack } from '@chakra-ui/react';
import { useState, useEffect, lazy, Suspense } from 'react';
import SkeletonCard from './dashboard-skeleton-card';
import DashboardCard from './dashboard-card';
import { APEX_COLORS, DASHBOARD_COLORS } from './dashboard-colors';

// Lazy load ApexCharts
const ReactApexChart = lazy(() => import('react-apexcharts'));

interface SentimentChartProps {
  sentimentData: {
    positive: number;
    neutral: number;
    negative: number;
  };
  isLoading: boolean;
}

export default function SentimentChart({
  sentimentData,
  isLoading,
}: SentimentChartProps) {
  const [chartOptions, setChartOptions] = useState({});
  const [chartSeries, setChartSeries] = useState<number[]>([]);

  useEffect(() => {
    if (!sentimentData) return;

    // Prepare data for the chart
    const chartData = {
      options: {
        chart: {
          id: 'sentiment-chart',
          toolbar: {
            show: false,
          },
        },
        labels: ['Positive', 'Neutral', 'Negative'],
        colors: APEX_COLORS.sentiment,
        legend: {
          position: 'bottom',
          markers: {
            shape: 'circle',
            size: 8,
          },
          fontSize: '14px',
          fontWeight: 500,
          offsetY: 5,
        },
        dataLabels: {
          enabled: false,
        },
        plotOptions: {
          pie: {
            donut: {
              size: '60%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Positive',
                  fontSize: '16px',
                  fontWeight: 500,
                  color: DASHBOARD_COLORS.neutral,
                  formatter: function () {
                    return sentimentData.positive + '%';
                  },
                },
                value: {
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#1a202c',
                  formatter: function (val: string | number) {
                    return val + '%';
                  },
                },
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: function (val: number) {
              return val + '%';
            },
          },
        },
      },
      series: [
        sentimentData.positive,
        sentimentData.neutral,
        sentimentData.negative,
      ],
    };

    setChartOptions(chartData.options);
    setChartSeries(chartData.series);
  }, [sentimentData]);

  if (isLoading) {
    return <SkeletonCard />;
  }

  if (!sentimentData) {
    return (
      <DashboardCard title="Overall Sentiment" height="350px">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Text color="gray.500">No sentiment data available</Text>
        </Box>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard title="Overall Sentiment">
      <VStack gap={4} height="100%">
        {/* Summary stats */}
        <HStack justify="space-around" width="100%" py={2}>
          <VStack gap={1}>
            <Text fontSize="xs" color="gray.500" fontWeight="medium">
              POSITIVE
            </Text>
            <HStack>
              <Box
                w={3}
                h={3}
                bg={DASHBOARD_COLORS.positive}
                borderRadius="full"
              />
              <Text fontSize="lg" fontWeight="bold" color="gray.800">
                {sentimentData.positive}%
              </Text>
            </HStack>
          </VStack>

          <VStack gap={1}>
            <Text fontSize="xs" color="gray.500" fontWeight="medium">
              NEUTRAL
            </Text>
            <HStack>
              <Box
                w={3}
                h={3}
                bg={DASHBOARD_COLORS.warning}
                borderRadius="full"
              />
              <Text fontSize="lg" fontWeight="bold" color="gray.800">
                {sentimentData.neutral}%
              </Text>
            </HStack>
          </VStack>

          <VStack gap={1}>
            <Text fontSize="xs" color="gray.500" fontWeight="medium">
              NEGATIVE
            </Text>
            <HStack>
              <Box
                w={3}
                h={3}
                bg={DASHBOARD_COLORS.error}
                borderRadius="full"
              />
              <Text fontSize="lg" fontWeight="bold" color="gray.800">
                {sentimentData.negative}%
              </Text>
            </HStack>
          </VStack>
        </HStack>

        {/* Chart */}
        <Box flex="1" width="100%">
          <Suspense fallback={<SkeletonCard />}>
            <ReactApexChart
              options={chartOptions}
              series={chartSeries}
              type="donut"
              height={280}
            />
          </Suspense>
        </Box>
      </VStack>
    </DashboardCard>
  );
}
